import React from 'react';
import {
  TextCustom,
  TextAppearance,
  TextWeight,
  TextSize,
  TextLineHeight,
  CustomImage,
  TextColor,
} from '@vw-marketing/us-components';
import {
  StyledTextWrapper,
  StyledImagePlaceholder,
  StyledImageWrapper,
  StyledImageGroup,
  OverallRatingTextWrapper,
  FiveStarsWrapper,
  FiveStarsTextWrapper,
  FiveStarsDisclaimerWrapper,
  ModelCardContainer,
  StyledReviewsLabelWrapper,
} from './styled';
import { ReviewModel } from '../../../typings/reviews';
import FiveStars from '../../../components/five-stars';
import { ImageModel } from '../../../typings/images-model-map';
import { AppConstants } from '../../../utils/app-constants';
import { transformIrisUrl } from '../../../utils/general';
import { useReviewModel } from '../../../services/reviews-data-service';
import { useFeatureAppConfig } from '../../../services/use-feature-app-config';

interface ModelCardProps {
  model?: ImageModel;
  review?: ReviewModel;
}

const ModelExtendedCard: React.FC<ModelCardProps> = (props): JSX.Element => {
  const { model } = props;
  const { fiveStarsConfig } = useFeatureAppConfig();
  // Component constants
  const reviewLabel = 'Reviews';
  const overallRatingLabel = 'Overall Rating';
  const reviewsDisclaimer =
    fiveStarsConfig?.viewAllDisclaimer || AppConstants.allReviewsDisclaimer;
  const review = useReviewModel();
  return (
    <ModelCardContainer>
      <StyledImageGroup>
        <StyledTextWrapper className="notranslate">
          <TextCustom
            appearance={TextAppearance.headline400}
            style={{
              fontSize: TextSize.TextSize32,
              fontWeight: TextWeight.bold,
              color: TextColor.inherit,
            }}
          >
            {model?.name}
          </TextCustom>
        </StyledTextWrapper>
        <StyledImagePlaceholder>
          <StyledImageWrapper className="image-wrapper">
            <CustomImage
              alt={model?.carImageAlt || ''}
              srcPlaceholder={AppConstants.PlaceHolder480x216 as string}
              src={
                transformIrisUrl(
                  model?.carImage || '',
                  AppConstants.IrisImgPositionE13,
                  '480',
                  '216',
                  model?.modelYear,
                  model?.slug,
                  model?.isNewImage,
                ) || AppConstants.PlaceHolder480x216
              }
            />
          </StyledImageWrapper>
        </StyledImagePlaceholder>
      </StyledImageGroup>
      <OverallRatingTextWrapper>
        <TextCustom
          appearance={TextAppearance.headline200}
          style={{
            fontSize: TextSize.TextSize16,
            lineHeight: TextLineHeight.TextLineHeight24,
            color: TextColor.primary,
            fontWeight: TextWeight.bold,
          }}
        >
          {overallRatingLabel}
        </TextCustom>
      </OverallRatingTextWrapper>
      <FiveStarsWrapper>
        <FiveStars
          percent={((review?.averageRating || 0) / 5) * 100}
          starSize={'18px'}
          starColor={AppConstants.StarColor as string}
          background={AppConstants.StarBackground as string}
        />
        <FiveStarsTextWrapper>
          <TextCustom
            appearance={TextAppearance.headline200}
            style={{
              fontSize: TextSize.TextSize16,
              lineHeight: TextLineHeight.TextLineHeight24,
              color: TextColor.inherit,
              fontWeight: TextWeight.regular,
            }}
          >
            {review?.averageRating.toFixed(1)}
            {' | '}
            {`${review?.page.totalElements} reviews`}
          </TextCustom>
        </FiveStarsTextWrapper>
      </FiveStarsWrapper>
      <StyledReviewsLabelWrapper>
        <TextCustom
          appearance={TextAppearance.headline200}
          style={{
            fontSize: TextSize.TextSize32,
            lineHeight: TextLineHeight.TextLineHeight40,
            fontWeight: TextWeight.bold,
          }}
        >
          {reviewLabel}
        </TextCustom>
      </StyledReviewsLabelWrapper>
      <FiveStarsDisclaimerWrapper>
        <TextCustom
          appearance={TextAppearance.headline200}
          style={{
            fontSize: TextSize.TextSize14,
            lineHeight: TextLineHeight.TextLineHeight22,
            fontWeight: TextWeight.regular,
            color: TextColor.inherit,
          }}
        >
          {reviewsDisclaimer}
        </TextCustom>
      </FiveStarsDisclaimerWrapper>
    </ModelCardContainer>
  );
};

export default ModelExtendedCard;
